import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { formatSaleDateNoYear } from '../utils/formatters';
import useSoldScan from '../hooks/useSoldScan';
import SoldScanModal from './modals/SoldScanModal';


ModuleRegistry.registerModules([AllCommunityModule]);

function SoldInv() {
  const { user, fetchWrapper } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const clientId = user?.id || 'Not authenticated';

  const [sales, setSales] = useState([]);
  const [selectedSaleId, setSelectedSaleId] = useState('');
  const [sections, setSections] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState('all');
  const [inventory, setInventory] = useState([]);
  const [status, setStatus] = useState('');
  const [qrMessage, setQrMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('unsold');
  const [itemCounts, setItemCounts] = useState({ available: 0, sold: 0 });
  const saleStorageKey = 'soldInvSelectedSaleId';

  const gridRef = useRef(null);

  const manualTagsSectionName = 'Manual Tags';

  // Image modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  // Search text for title filter
  const [searchText, setSearchText] = useState('');
  const [displayedItemCount, setDisplayedItemCount] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState({ currentPage: 0, totalPages: 1 });
  const [selectedSellCount, setSelectedSellCount] = useState(0);
  const [selectedSellIds, setSelectedSellIds] = useState([]);
  const [, setScanLaunchMode] = useState('scan');
  const statusIsError = useMemo(
    () => /error|failed|maximum|must|sale failed|select one or more/i.test(String(status || '')),
    [status]
  );

  // ---------- Numeric helpers ----------
  const toNumberOrNull = useCallback((val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'number') return Number.isFinite(val) ? val : null;
    const s = String(val).trim();
    if (!s) return null;
    const cleaned = s.replace(/,/g, '').replace(/[^\d.-]/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.')
      return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }, []);

  const toIntOr = useCallback((val, fallback = 1) => {
    const n = toNumberOrNull(val);
    return n === null ? fallback : Math.max(1, Math.floor(n));
  }, [toNumberOrNull]);

  const calculateCheckoutUnitPrice = useCallback((listPrice, discountPct) => {
    if (listPrice === null || listPrice === undefined) return null;
    const price = Number(listPrice);
    if (!Number.isFinite(price)) return null;
    const normalizedDiscount = Math.max(0, Math.min(100, Number(discountPct) || 0));
    const discountedPrice = Number((price * (1 - normalizedDiscount / 100)).toFixed(2));
    if (discountedPrice > 0) return discountedPrice;
    return price > 0 ? price : discountedPrice;
  }, []);

  const normalizeSearchText = useCallback(
    (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim(),
    []
  );

  const titleMatchesOrderedSearch = useCallback((title, rawSearch) => {
    const normalizedTitle = normalizeSearchText(title);
    const normalizedSearch = normalizeSearchText(rawSearch);
    if (!normalizedSearch) return true;

    const titleWords = normalizedTitle.split(/[^a-z0-9]+/).filter(Boolean);
    const terms = normalizedSearch.split(/[^a-z0-9]+/).filter(Boolean);
    if (terms.length === 0) return true;

    let wordCursor = 0;
    for (const term of terms) {
      let foundAt = -1;
      for (let i = wordCursor; i < titleWords.length; i += 1) {
        if (titleWords[i].startsWith(term)) {
          foundAt = i;
          break;
        }
      }
      if (foundAt === -1) return false;
      wordCursor = foundAt + 1;
    }
    return true;
  }, [normalizeSearchText]);

  const formatUSD = useCallback((val) => {
    const n = toNumberOrNull(val);
    return n === null
      ? ''
      : new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(n);
  }, [toNumberOrNull]);

  const API_URL = process.env.REACT_APP_API_URL;

  // ---------- Fetch sales (active only) ----------
  const fetchSales = useCallback(async () => {
    if (!user) return;
    try {
      const response = await fetchWrapper(
        `${API_URL}/supabase/rpc/select_all_sales`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ p_client_id: clientId }),
        }
      );
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        const activeSales = data
          .filter((sale) => sale.status === 'active')
          .map((sale) => ({
            ...sale,
            disc_pct: toNumberOrNull(sale.disc_pct) ?? 0,
            sales_tax: toNumberOrNull(sale.sales_tax) ?? 0,
          }));
        const sortedActiveSales = [...activeSales].sort((a, b) => {
          const aTs = Date.parse(a?.start_date || '');
          const bTs = Date.parse(b?.start_date || '');
          const aValid = Number.isFinite(aTs);
          const bValid = Number.isFinite(bTs);
          if (aValid && bValid) return bTs - aTs; // newest first
          if (aValid) return -1;
          if (bValid) return 1;
          return String(a?.sale_name || '').localeCompare(String(b?.sale_name || ''), undefined, { sensitivity: 'base' });
        });
        setSales(sortedActiveSales);
      } else {
        console.error('Failed to fetch sales:', data);
        setStatus(
          'Error fetching sales. Contact support@scucciai.com'
        );
      }
    } catch (error) {
      console.error('Error fetching sales:', error);
      setStatus(
        `Error fetching sales: ${error.message}. Contact support@scucciai.com`
      );
    }
  }, [API_URL, clientId, fetchWrapper, toNumberOrNull, user]);

  useEffect(() => {
    if (!selectedSaleId) return;
    sessionStorage.setItem(saleStorageKey, selectedSaleId);
  }, [selectedSaleId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const saleIdFromUrl = params.get('saleId');
    if (
      saleIdFromUrl &&
      saleIdFromUrl !== selectedSaleId &&
      sales.some((sale) => sale.id === saleIdFromUrl)
    ) {
      setSelectedSaleId(saleIdFromUrl);
    }
  }, [location.search, sales, selectedSaleId]);

  useEffect(() => {
    if (selectedSaleId || sales.length === 0) return;
    const saleIdFromUrl = new URLSearchParams(location.search).get('saleId');
    if (saleIdFromUrl && sales.some((sale) => sale.id === saleIdFromUrl)) return;
    const savedSaleId = sessionStorage.getItem(saleStorageKey);
    if (savedSaleId && sales.some((sale) => sale.id === savedSaleId)) {
      setSelectedSaleId(savedSaleId);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const closestSale = sales.reduce((closest, sale) => {
      const date = new Date(sale.start_date);
      if (Number.isNaN(date.getTime())) return closest;
      date.setHours(0, 0, 0, 0);
      const distance = Math.abs(date.getTime() - today.getTime());
      if (!closest || distance < closest.distance) {
        return { id: sale.id, distance };
      }
      return closest;
    }, null);

    if (closestSale?.id) {
      setSelectedSaleId(closestSale.id);
    }
  }, [location.search, sales, selectedSaleId]);

  // ---------- Fetch sections for selected sale ----------
  const fetchSections = useCallback(
    async (saleId) => {
      if (!saleId || !user) {
        setSections([]);
        return;
      }
      try {
        const response = await fetchWrapper(
          `${API_URL}/supabase/rpc/select_sale_sections_by_sale_id`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ p_sale_id: saleId }),
          }
        );
        if (!response) throw new Error('Sections fetch failed');
        const sectionsData = await response.json();
        const sortedSections = sectionsData.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setSections(sortedSections);
        if (sortedSections.length === 0) {
          setStatus(
            '⚠️ No sections for this sale. Upload images to create.'
          );
        } else {
          setStatus('');
        }
      } catch (error) {
        console.error('Sections fetch error:', error);
        setStatus(`❌ Failed to load sections: ${error.message}`);
        setSections([]);
      }
    },
    [API_URL, fetchWrapper, user]
  );

  const fetchCategoryOptions = useCallback(async () => {
    try {
      const response = await fetchWrapper(
        `${API_URL}/supabase/rpc/select_inventory_categories`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to load categories');
      }
      const rows = await response.json();
      const values = (Array.isArray(rows) ? rows : [])
        .map((row) => String(row?.category || '').trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      setCategoryOptions(values);
    } catch (error) {
      console.error('SoldInv fetch categories error:', error);
      setCategoryOptions([]);
    }
  }, [API_URL, fetchWrapper]);

  const resolveManualTagsSection = useCallback(async () => {
    const existing = sections.find(
      (section) =>
        (section.name || '').trim().toLowerCase() === manualTagsSectionName.toLowerCase()
    );
    if (existing) {
      return { sectionId: existing.id, sectionName: existing.name };
    }
    const response = await fetchWrapper(
      `${API_URL}/supabase/rpc/insert_sale_section`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          p_sale_id: selectedSaleId,
          p_name: manualTagsSectionName,
        }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create manual tags section');
    }
    const newSectionId = await response.json();
    await fetchSections(selectedSaleId);
    return { sectionId: newSectionId, sectionName: manualTagsSectionName };
  }, [API_URL, fetchSections, fetchWrapper, manualTagsSectionName, sections, selectedSaleId]);

  // ---------- Fetch inventory (unsold or sold, sorted by title) ----------
  const fetchInventory = useCallback(async () => {
  if (!selectedSaleId) {
    setInventory([]);
    return;
  }
  setLoading(true);
  setStatus('Loading inventory...');
  try {
    // Decide which statuses to load based on viewMode
    // UNSOLD INV = active + on hold
    // SOLD INV = sold
    const statuses =
      viewMode === 'unsold' ? ['active', 'on hold'] : ['sold'];

    const body = {
      p_sale_id: selectedSaleId,
      p_client_id: clientId,
      p_section_id: selectedSectionId !== 'all' ? selectedSectionId : null,
      p_statuses: statuses,
    };

    const itemResponse = await fetchWrapper(
      `${API_URL}/supabase/rpc/select_inventory_items_by_statuses`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );

    const data = await itemResponse.json();

    if (!itemResponse.ok || !Array.isArray(data)) {
      console.error('Failed to fetch inventory:', data);
      setStatus(
        `Error: Failed to load inventory: ${
          data.error?.message || 'Unknown error'
        }. Contact support@scucciai.com`
      );
      setInventory([]);
      return;
    }

    // Sort items by title alphabetically, preserving duplicates
    const sortedItems = [...data].sort((a, b) =>
      (a.title || '').localeCompare(b.title || '', undefined, {
        sensitivity: 'base',
      })
    );

    const inventoryData = sortedItems.map((item) => {
      const qty = toIntOr(item.quantity_estimate, 1);
      return {
        id: item.id || '',
        section_name: item.section_name || 'N/A',
        category: String(item.category || '').trim(),
        title: item.title || 'N/A',
        description: item.description || 'N/A',
        per_item_cost_estimate: toNumberOrNull(item.per_item_cost_estimate),
        quantity_estimate: qty,
        status: String(item.status || '').trim().toLowerCase(),
        tagged: item.tagged || false,
        url: item.signed_url || 'N/A',
        sold_price: toNumberOrNull(item.sold_price),
        // UI-only: quantity being sold in this operation
        qty_sold: qty === 1 ? 1 : 0,
      };
    });

    setInventory(inventoryData);
    setStatus('');
  } catch (error) {
    console.error('Error fetching inventory:', error);
    setStatus(
      `Error: Failed to load inventory: ${error.message}. Contact support@scucciai.com`
    );
    setInventory([]);
  } finally {
    setLoading(false);
  }
  }, [
    API_URL,
    clientId,
    selectedSaleId,
    selectedSectionId,
    fetchWrapper,
    toIntOr,
    toNumberOrNull,
    viewMode, // 👈 important: refetch when viewMode changes
  ]);

  const fetchItemCounts = useCallback(async () => {
    if (!selectedSaleId) {
      setItemCounts({ available: 0, sold: 0 });
      return;
    }
    try {
      const commonBody = {
        p_sale_id: selectedSaleId,
        p_client_id: clientId,
        p_section_id: selectedSectionId !== 'all' ? selectedSectionId : null,
      };

      const [availableResp, soldResp] = await Promise.all([
        fetchWrapper(`${API_URL}/supabase/rpc/select_inventory_items_by_statuses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...commonBody,
            p_statuses: ['active', 'on hold'],
          }),
        }),
        fetchWrapper(`${API_URL}/supabase/rpc/select_inventory_items_by_statuses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...commonBody,
            p_statuses: ['sold'],
          }),
        }),
      ]);

      const availableData = await availableResp.json().catch(() => []);
      const soldData = await soldResp.json().catch(() => []);

      setItemCounts({
        available: availableResp.ok && Array.isArray(availableData) ? availableData.length : 0,
        sold: soldResp.ok && Array.isArray(soldData) ? soldData.length : 0,
      });
    } catch {
      setItemCounts({ available: 0, sold: 0 });
    }
  }, [API_URL, clientId, fetchWrapper, selectedSaleId, selectedSectionId]);

  const {
    scanModalOpen,
    scanItems,
    manualLines,
    manualOpen,
    scanError,
    scanErrorItemId,
    showScanner,
    scanInProgress,
    scanTotal,
    hasMultipleSales,
    openScanModal,
    closeScanModal,
    startScan,
    stopScan,
    addManualLine,
    updateManualLine,
    removeManualLine,
    openManualItems,
    updateScanItem,
    removeScanItem,
    loadSelectedItemsIntoModal,
    handleConfirmScan,
    handleQrParam,
  } = useSoldScan({
    apiUrl: API_URL,
    clientId,
    fetchWrapper,
    selectedSaleId,
    saleDiscPct: toNumberOrNull(sales.find((s) => s.id === selectedSaleId)?.disc_pct) ?? 0,
    setSelectedSaleId,
    setSelectedSectionId,
    fetchInventory,
    resolveManualSection: resolveManualTagsSection,
    toNumberOrNull,
    toIntOr,
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('qr');
    if (!token) {
      setQrMessage('');
      return;
    }
    setViewMode('unsold');
    setQrMessage(user ? '' : 'Sign in to resolve the QR code.');
    handleQrParam(token, Boolean(user));
  }, [location.search, handleQrParam, user]);

  // ---------- Close image modal on Escape ----------
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
        setSelectedImage('');
      }
    };
    if (showModal) {
      document.addEventListener('keydown', handleKey);
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [showModal]);

  // ---------- Reset search when sale/section changes ----------
  useEffect(() => {
    setSearchText('');
    setDisplayedItemCount(0);
  }, [selectedSaleId, selectedSectionId]);

  useEffect(() => {
    setSelectedSellCount(0);
    setSelectedSellIds([]);
  }, [selectedSaleId, selectedSectionId, viewMode]);

  useEffect(() => {
    if (viewMode !== 'sold') return;
    try {
      gridRef.current?.api?.deselectAll?.();
    } catch {
    }
  }, [viewMode]);

  // ---------- Column defs (Thumbnail, Title, Asking Price, Qty) ----------

 const quickColumnDefs = useMemo(() => {
  const thumbCol = {
    field: 'url',
    headerName: 'Image',
    headerClass: 'center-header border-r border-gray-200',
    cellClass: 'center-cell border-r border-gray-100',
    width: 92,
    minWidth: 84,
    maxWidth: 92,
    sortable: false,
    suppressSizeToFit: true,
    cellStyle: { textAlign: 'left' },
    cellRenderer: (params) =>
      params.value && params.value !== 'N/A' ? (
        <img
          src={params.value}
          alt="item thumbnail"
          style={{
            width: '100%',
            height: '52px',
            objectFit: 'cover',
            cursor: 'pointer',
            borderRadius: '4px',
          }}
          onClick={() => {
            setSelectedImage(params.value);
            setShowModal(true);
          }}
          onError={(e) =>
            console.log('Image load error for URL:', e.target.src)
          }
          loading="lazy"
        />
      ) : (
        'N/A'
      ),
  };

  const titleCol = {
    field: 'title',
    headerName: 'Title',
    headerClass: 'center-header border-r border-gray-200',
    cellClass: 'title-vcenter border-r border-gray-100',
    width: 200,
    minWidth: 150,
    maxWidth: 240,
    sortable: true,
    sort: 'asc', // initial sort by title
    editable: true,
    wrapText: true,
    autoHeight: true,
    cellStyle: () => ({
      whiteSpace: 'normal',
      lineHeight: '1.2',
      fontSize: '1.15em',
      textAlign: 'left',
    }),
    cellEditor: 'agLargeTextCellEditor',
    cellEditorParams: {
      rows: 3,
      cols: 36,
      resize: 'vertical',
    },
    valueParser: (p) => String(p.newValue ?? '').trim(),
  };

  const askingPriceCol = {
    field: 'per_item_cost_estimate',
    headerName: viewMode === 'sold' ? 'Sold Price' : 'Tag Price',
    width: 95,
    minWidth: 88,
    maxWidth: 100,
    headerClass: 'center-header',
    valueGetter: (params) => (
      viewMode === 'sold'
        ? params?.data?.sold_price
        : params?.data?.per_item_cost_estimate
    ),
    valueFormatter: (p) => formatUSD(p.value),
    cellStyle: {
      textAlign: 'center',
      fontVariantNumeric: 'tabular-nums',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  const qtyCol = {
    field: 'quantity_estimate',
    headerName: 'Qty',
    headerClass: 'center-header',
    cellClass: 'border-r border-gray-100',
    width: 52,
    minWidth: 46,
    maxWidth: 58,
    sortable: true,
    editable: false, // read-only; in SOLD view this is "qty sold"
    suppressSizeToFit: true,
    valueFormatter: (p) => String(toIntOr(p.value, 1)),
    cellStyle: () => ({
      textAlign: 'center',
      fontVariantNumeric: 'tabular-nums',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
  };

  // Base columns always shown
  const cols = [thumbCol, titleCol];

  // Asking Price + Qty always shown
  cols.push(askingPriceCol, qtyCol);

  return cols;
}, [formatUSD, toIntOr, viewMode]);

  // ---------- Cell edit handler ----------
  const handleCellUpdate = useCallback(
    async (params) => {
      const {
        data: rowData,
        colDef: { field },
        newValue,
        oldValue,
      } = params;

      if (newValue === oldValue) return;
      if (!rowData?.id) return;

      let rpcEndpoint = null;
      const updateData = {
        p_id: rowData.id,
        p_sale_id: selectedSaleId,
        p_client_id: clientId,
      };
      let finalValue = newValue;

      const revertField = (fieldName, valueToRevert) => {
        if (gridRef.current?.api) {
          const api = gridRef.current.api;
          const rowNode = api.getRowNode(rowData.id);
          const current = rowNode?.data || rowData;
          api.applyTransaction({
            update: [{ ...current, [fieldName]: valueToRevert }],
          });
        }
      };

      try {
        switch (field) {
          case 'title': {
            rpcEndpoint = 'update_inventory_item_title';
            updateData.p_title = String(finalValue ?? '').trim();
            finalValue = updateData.p_title;
            break;
          }

          case 'per_item_cost_estimate': {
            rpcEndpoint =
              'update_inventory_item_per_item_cost_estimate';
            const n = toNumberOrNull(finalValue);
            updateData.p_per_item_cost_estimate = n;
            finalValue = n;
            break;
          }

          case 'quantity_estimate':
            // Read-only on this screen; ignore any changes
            return;

          case 'qty_sold': {
            // UI-only: validate and update local row, no RPC
            const qty = toIntOr(finalValue, null);
            const maxQty = toIntOr(rowData.quantity_estimate, 1);
            if (
              maxQty > 1 &&
              (qty === null || qty <= 0 || qty > maxQty)
            ) {
              setStatus(
                `Maximum quantity of ${maxQty}.`
              );
              revertField('qty_sold', rowData.qty_sold);
              return;
            }

            if (gridRef.current?.api) {
              const api = gridRef.current.api;
              const rowNode = api.getRowNode(rowData.id);
              const current = rowNode?.data || rowData;
              api.applyTransaction({
                update: [{ ...current, qty_sold: qty }],
              });
            }
            return;
          }

                case 'sold_price': {
        // This is the SELL operation via record_inventory_item_sale
        const soldPrice = toNumberOrNull(finalValue);
        if (soldPrice === null || soldPrice <= 0) {
          setStatus('Sold Price must be a positive number.');
          revertField('sold_price', oldValue);
          return;
        }

        const itemQty = toIntOr(rowData.quantity_estimate, 1);
        let qtySold;

        if (itemQty === 1) {
          // Singles: always treat as 1
          qtySold = 1;
        } else {
          // Multiples: require a valid qty_sold
          const n = toNumberOrNull(rowData.qty_sold);
          if (
            n == null ||
            !Number.isFinite(n) ||
            n <= 0 ||
            !Number.isInteger(n) ||
            n > itemQty
          ) {
            setStatus(
              `For Qty ${itemQty}, you must enter a Qty Sold between 1 and ${itemQty} before entering Sold Price.`
            );
            revertField('sold_price', oldValue);
            return;
          }
          qtySold = n;
        }

        // Call the new sale RPC (single transaction)
        const resp = await fetchWrapper(
          `${API_URL}/supabase/rpc/record_inventory_item_sale`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              p_id: rowData.id,
              p_sale_id: selectedSaleId,
              p_client_id: clientId,
              p_qty_sold: qtySold,
              p_total_sold_price: soldPrice,
            }),
          }
        );

        if (!resp.ok) {
          const errData = await resp.json();
          console.error('record_inventory_item_sale error:', errData);
          setStatus(
            `Sale failed: ${
              errData.error?.message || 'Unknown error'
            }`
          );
          revertField('sold_price', oldValue);
          return;
        }

        // Success: refresh inventory
        await fetchInventory();
        return;
      }


          default:
            // Ignore unsupported fields
            return;
        }

        // For simple updates (title, price)
        if (!rpcEndpoint) return;

        const response = await fetchWrapper(
          `${API_URL}/supabase/rpc/${rpcEndpoint}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateData),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Update error:', errorData);
          setStatus(
            `Update failed: ${
              errorData.error?.message || 'Unknown error'
            }`
          );
          revertField(field, oldValue);
          return;
        }

        // Apply updated value in grid
        if (gridRef.current?.api) {
          const api = gridRef.current.api;
          const rowNode = api.getRowNode(rowData.id);
          const current = rowNode?.data || rowData;
          const updatedRow = { ...current, [field]: finalValue };
          api.applyTransaction({ update: [updatedRow] });
        }
      } catch (error) {
        console.error('Cell update error:', error);
        setStatus(`Update error: ${error.message}`);
        revertField(field, oldValue);
      }
    },
    [API_URL, clientId, selectedSaleId, fetchInventory, fetchWrapper, toIntOr, toNumberOrNull]
  );

  // ---------- Effects ----------
  useEffect(() => {
    if (user) {
      fetchSales();
      fetchCategoryOptions();
    }
  }, [user, fetchCategoryOptions, fetchSales]);

  useEffect(() => {
    if (selectedSaleId) {
      fetchSections(selectedSaleId);
      fetchInventory();
      fetchItemCounts();
    } else {
      setSections([]);
      setInventory([]);
      setSelectedSectionId('all');
      setItemCounts({ available: 0, sold: 0 });
    }
  }, [selectedSaleId, fetchSections, fetchInventory, fetchItemCounts]);

  useEffect(() => {
    if (selectedSaleId) {
      fetchInventory();
      fetchItemCounts();
    }
  }, [selectedSectionId, selectedSaleId, fetchInventory, fetchItemCounts]);

  useEffect(() => {
    if (selectedSectionId === 'all') return;
    const sectionExists = sections.some((section) => section.id === selectedSectionId);
    if (!sectionExists) {
      setSelectedSectionId('all');
    }
  }, [sections, selectedSectionId]);

  const filteredInventory = useMemo(() => {
    const rows = Array.isArray(inventory) ? inventory : [];
    const normalizedSearch = normalizeSearchText(searchText);
    if (!normalizedSearch) return rows;

    const selectedIdSet = new Set(selectedSellIds);
    const pinnedSelectedRows = [];
    const matchedRows = [];

    for (const row of rows) {
      const rowId = String(row?.id || '').trim();
      if (rowId && selectedIdSet.has(rowId)) {
        pinnedSelectedRows.push(row);
        continue;
      }
      if (titleMatchesOrderedSearch(row?.title, normalizedSearch)) {
        matchedRows.push(row);
      }
    }

    return [...pinnedSelectedRows, ...matchedRows];
  }, [inventory, normalizeSearchText, searchText, selectedSellIds, titleMatchesOrderedSearch]);

  const updateDisplayedItemCount = useCallback((apiOverride = null) => {
    const api = apiOverride || gridRef.current?.api;
    const nextCount = api?.getDisplayedRowCount
      ? api.getDisplayedRowCount()
      : filteredInventory.length;
    setDisplayedItemCount((prev) => (prev === nextCount ? prev : nextCount));
  }, [filteredInventory.length]);

  const updatePaginationInfo = useCallback((apiOverride = null) => {
    const api = apiOverride || gridRef.current?.api;
    if (!api || typeof api.paginationGetCurrentPage !== 'function') {
      setPaginationInfo((prev) => (
        prev.currentPage === 0 && prev.totalPages === 1
          ? prev
          : { currentPage: 0, totalPages: 1 }
      ));
      return;
    }

    const currentPage = api.paginationGetCurrentPage?.() ?? 0;
    const totalPagesRaw = api.paginationGetTotalPages?.() ?? 0;
    const totalPages = Math.max(1, totalPagesRaw || 1);
    setPaginationInfo((prev) => (
      prev.currentPage === currentPage && prev.totalPages === totalPages
        ? prev
        : { currentPage, totalPages }
    ));
  }, []);

  const onGridReady = useCallback((params) => {
    if (!params?.api) return;
    updateDisplayedItemCount(params.api);
    updatePaginationInfo(params.api);
  }, [updateDisplayedItemCount, updatePaginationInfo]);

  const handleGridModelUpdated = useCallback((params) => {
    updateDisplayedItemCount(params?.api || null);
    updatePaginationInfo(params?.api || null);
  }, [updateDisplayedItemCount, updatePaginationInfo]);

  const handlePaginationChanged = useCallback((params) => {
    updatePaginationInfo(params?.api || null);
  }, [updatePaginationInfo]);


  const currentSale = useMemo(
    () => sales.find((s) => s.id === selectedSaleId),
    [sales, selectedSaleId]
  );
  const currentSaleName = currentSale?.sale_name || 'Sale';
  const saleDiscPct = toNumberOrNull(currentSale?.disc_pct) ?? 0;
  const saleSalesTaxPct = toNumberOrNull(currentSale?.sales_tax) ?? 0;
  const saleCcCollectionEnabled = Boolean(currentSale?.cc_collection_enabled);

  const handleSectionChange = (e) => {
    setSelectedSectionId(e.target.value);
  };

  const handleSellItems = useCallback(async () => {
    const selectedRows = gridRef.current?.api?.getSelectedRows?.() || [];
    if (!selectedRows.length) {
      setStatus('Select one or more items to sell.');
      return;
    }
    setScanLaunchMode('sell');
    const result = await loadSelectedItemsIntoModal(selectedRows);
    if (result?.added > 0) {
      setStatus('');
      try {
        gridRef.current?.api?.deselectAll?.();
        setSelectedSellCount(0);
        setSelectedSellIds([]);
      } catch {
      }
    }
  }, [loadSelectedItemsIntoModal]);
  const handleSellSelectionChanged = useCallback((params) => {
    const selectedRows = params?.api?.getSelectedRows?.() || [];
    const count = selectedRows.length;
    const ids = selectedRows
      .map((row) => String(row?.id || '').trim())
      .filter(Boolean);
    setSelectedSellCount(count);
    setSelectedSellIds(ids);
  }, []);
  const handleScanTags = useCallback(() => {
    setScanLaunchMode('scan');
    openScanModal();
    startScan();
  }, [openScanModal, startScan]);

  const handleSearchChange = (e) => {
    const value = e.target.value || '';
    setSearchText(value);
  };

  const handleClearSearch = useCallback(() => {
    setSearchText('');
  }, []);

  useEffect(() => {
    updateDisplayedItemCount();
  }, [filteredInventory, updateDisplayedItemCount]);

  useEffect(() => {
    updatePaginationInfo();
  }, [filteredInventory, updatePaginationInfo]);


  const closeModal = () => {
    setShowModal(false);
    setSelectedImage('');
  };

  const scanSubtotal = scanTotal;
  const scanSalesTaxAmount = scanSubtotal * (Math.max(0, saleSalesTaxPct) / 100);
  const manualTitleOptions = useMemo(() => {
    const discountPct = Math.max(0, Math.min(100, Number(saleDiscPct) || 0));
    const options = [];
    for (const row of inventory) {
      const status = String(row?.status || '').trim().toLowerCase();
      if (status && status !== 'active' && status !== 'on hold') continue;
      const itemId = String(row?.id || '').trim();
      const title = String(row?.title || '').trim();
      if (!itemId || !title) continue;
      const category = String(row?.category || '').trim();
      const basePrice = toNumberOrNull(row?.per_item_cost_estimate);
      const discountedPrice = calculateCheckoutUnitPrice(basePrice, discountPct);
      const qtyAvailable = toIntOr(row?.quantity_estimate, 1);
      options.push({
        id: itemId,
        title,
        category,
        price: discountedPrice,
        discountedPrice,
        tagPrice: basePrice,
        priceIncludesDiscount: true,
        qtyAvailable,
      });
    }
    return options.sort((a, b) => {
      const byTitle = a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
      if (byTitle !== 0) return byTitle;
      return a.id.localeCompare(b.id);
    });
  }, [calculateCheckoutUnitPrice, inventory, saleDiscPct, toIntOr, toNumberOrNull]);
  const manualTitlePriceByTitle = useMemo(() => {
    const stats = new Map();
    const discountPct = Math.max(0, Math.min(100, Number(saleDiscPct) || 0));
    for (const row of inventory) {
      const status = String(row?.status || '').trim().toLowerCase();
      if (status && status !== 'active' && status !== 'on hold') continue;
      const title = String(row?.title || '').trim();
      if (!title) continue;
      const key = title.toLowerCase();
      const parsed = Number(row?.per_item_cost_estimate);
      const discounted = calculateCheckoutUnitPrice(parsed, discountPct);
      const price = discounted;
      const existing = stats.get(key);
      if (!existing) {
        stats.set(key, { count: 1, price });
      } else {
        existing.count += 1;
        stats.set(key, existing);
      }
    }
    const result = {};
    for (const [key, value] of stats.entries()) {
      if (value.count !== 1) continue;
      if (value.price === null || value.price <= 0) continue;
      result[key] = Number(value.price.toFixed(2));
    }
    return result;
  }, [calculateCheckoutUnitPrice, inventory, saleDiscPct]);

  const inventoryItemById = useMemo(() => {
    const byId = new Map();
    for (const row of inventory) {
      const itemId = String(row?.id || '').trim();
      if (!itemId) continue;
      byId.set(itemId, row);
    }
    return byId;
  }, [inventory]);

  const handleUpdateManualLine = useCallback((id, field, value) => {
    updateManualLine(id, field, value);

    if (field !== 'inventoryItemId') return;
    const itemId = String(value || '').trim();
    if (!itemId) return;

    const matchedItem = inventoryItemById.get(itemId);
    if (!matchedItem) return;

    const basePrice = toNumberOrNull(matchedItem?.per_item_cost_estimate);
    if (basePrice !== null && basePrice > 0) {
      const discountPct = Math.max(0, Math.min(100, Number(saleDiscPct) || 0));
      const discountedPrice = calculateCheckoutUnitPrice(basePrice, discountPct);
      updateManualLine(id, 'price', discountedPrice.toFixed(2));
    }
  }, [calculateCheckoutUnitPrice, inventoryItemById, saleDiscPct, toNumberOrNull, updateManualLine]);
  const clearScanQueryParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const hasScanParams = params.has('qr') || params.has('token');
    if (!hasScanParams) return;

    params.delete('qr');
    params.delete('token');

    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  const handleCloseScanModal = useCallback(() => {
    closeScanModal();
    setScanLaunchMode('scan');
    clearScanQueryParams();
  }, [clearScanQueryParams, closeScanModal]);

  const handleConfirmScanAndClear = useCallback(async (checkoutSummary = {}) => {
    const didConfirm = await handleConfirmScan(checkoutSummary);
    if (didConfirm) {
      setScanLaunchMode('scan');
      clearScanQueryParams();
    }
    return didConfirm;
  }, [clearScanQueryParams, handleConfirmScan]);

  const salesGridWidth = 490;
  const sectionControlWidth = 280;

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 pt-0">
      <div className="flex-grow flex flex-col items-center justify-start">
        <div className="w-full mx-auto px-3">
          <h1 className="text-2xl font-bold text-center mb-6 -mt-2">
            Scucci Estate Sales Tracker
          </h1>

          {/* Sale Selector */}
          <div className="mb-3 mt-[5px] flex justify-center">
            <div className="w-[15rem]">
              <label
                htmlFor="sale-select"
                className="block text-sm font-medium text-gray-700 mb-2 text-center"
              >
                Select Active Sale:
              </label>
              <select
                id="sale-select"
                value={selectedSaleId}
                onChange={(e) => setSelectedSaleId(e.target.value)}
                aria-label="Select active sale"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">-- Select an Active Sale --</option>
                {sales.map((sale) => {
                  const startLabel = formatSaleDateNoYear(sale.start_date);
                  const endLabel = formatSaleDateNoYear(sale.end_date);
                  return (
                    <option key={sale.id} value={sale.id}>
                      {`${sale.sale_name} (${startLabel} - ${endLabel})`}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>


          {/* Section Selector */}
          {selectedSaleId && (
            <div className="mb-3 flex flex-col items-center gap-3">
              <div className="w-auto max-w-xs" style={{ width: sectionControlWidth }}>
                <label
                  htmlFor="section-select"
                  className="block text-sm font-medium text-gray-700 mb-2 text-center"
                >
                  Select Section:
                </label>
                <select
                  id="section-select"
                  value={selectedSectionId}
                  onChange={handleSectionChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="all">All Sections</option>
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

               {/* Status / messages */}
          {status && (
            <p className={`text-center text-sm mb-2 ${statusIsError ? 'text-red-500' : 'text-slate-600'}`}>
              {status}
            </p>
          )}
          {qrMessage && (
            <p className="text-center text-sm text-slate-600 mb-2">
              {qrMessage}
            </p>
          )}
          {loading && (
            <p className="text-center text-sm text-gray-700 mb-2">
              Loading...
            </p>
          )}

          {/* Main content for selected sale */}
          {!loading && selectedSaleId && (
            <div className="overflow-x-auto">
              <div className="mt-1 mb-6 flex justify-center">
                <div className="flex flex-col items-center gap-2">
                  <div
                    className="inline-flex items-center bg-white border border-gray-300 rounded-md shadow-sm overflow-hidden"
                    style={{ width: sectionControlWidth }}
                  >
                    <button
                      type="button"
                      onClick={() => setViewMode('unsold')}
                      className={[
                        'flex-1 px-5 py-2.5 text-sm font-bold text-center whitespace-nowrap',
                        'focus:outline-none focus:ring-2 focus:ring-blue-900',
                        viewMode === 'unsold'
                          ? 'bg-blue-900 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {`AVAILABLE (${itemCounts.available})`}
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('sold')}
                      className={[
                        'flex-1 px-5 py-2.5 text-sm font-bold text-center whitespace-nowrap',
                        'focus:outline-none focus:ring-2 focus:ring-blue-900',
                        viewMode === 'sold'
                          ? 'bg-blue-900 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {`SOLD (${itemCounts.sold})`}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/estate-sales-pipeline/sales-transactions${selectedSaleId ? `?saleId=${selectedSaleId}` : ''}`)}
                    className="h-10 px-4 rounded-lg border border-slate-300 bg-white text-slate-900 font-semibold text-sm shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-900"
                  >
                    See Sales Transactions
                  </button>
                </div>
              </div>

              {viewMode === 'unsold' && (
                <div className="mt-3 mb-4 w-full flex justify-center">
                  <div className="flex justify-center items-center gap-3" style={{ width: salesGridWidth }}>
                    <button
                      type="button"
                      onClick={handleSellItems}
                      disabled={selectedSellCount === 0}
                      className={[
                        'inline-flex flex-1 items-center justify-center px-5 py-2.5 text-sm font-semibold rounded-md border focus:outline-none focus:ring-2 focus:ring-offset-1',
                        selectedSellCount > 0
                          ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500'
                          : 'bg-slate-200 text-slate-500 border-slate-300 cursor-not-allowed focus:ring-slate-300',
                      ].join(' ')}
                      style={{ flex: 2 }}
                    >
                      {selectedSellCount > 0
                        ? `Go to Checkout for ${selectedSellCount} Item${selectedSellCount === 1 ? '' : 's'}`
                        : 'Select Items to Sell'}
                    </button>
                    <button
                      type="button"
                      onClick={handleScanTags}
                      className="inline-flex flex-1 items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-md border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      style={{ flex: 1 }}
                    >
                      Scan Tags
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-2 w-full flex justify-center">
                <div className="flex flex-col items-start" style={{ width: salesGridWidth }}>
                  <div className="w-full text-sm font-semibold text-gray-700 mb-1.5 text-left">Quick Title Search:</div>
                  <div className="w-full flex items-center gap-2">
                    <input
                      id="search-title"
                      type="text"
                      value={searchText}
                      onChange={handleSearchChange}
                      placeholder="e.g., Singer Sewing Machine"
                      className="inline-flex items-center px-3 py-2.5 text-sm font-semibold rounded-md border border-gray-400 text-gray-800 bg-white placeholder:text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
                      style={{ width: `calc(${Math.round(salesGridWidth / 3)}px + 10ch)` }}
                    />
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      disabled={!searchText}
                      className={[
                        'px-3 py-2.5 text-sm font-semibold rounded-md border transition-colors',
                        searchText
                          ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed',
                      ].join(' ')}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>

              {/* If there are no rows, show a mode-specific message instead of the grid */}
              {inventory.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-gray-500 text-sm">
                    {viewMode === 'unsold'
                      ? `No inventory found for ${currentSaleName}. Upload images to create items.`
                      : `No sold items yet for ${currentSaleName}. Enter sold prices in UNSOLD INV view first.`}
                  </p>
                </div>
              ) : filteredInventory.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-gray-500 text-sm">
                    No items match your title search.
                  </p>
                </div>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <div
                    className="mx-auto mb-2 rounded-md border border-slate-200 bg-white px-2 py-2.5 min-h-[56px] flex items-center gap-2"
                    style={{ width: salesGridWidth }}
                  >
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => gridRef.current?.api?.paginationGoToFirstPage?.()}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                        aria-label="First page"
                      >
                        {'<<'}
                      </button>
                      <button
                        type="button"
                        onClick={() => gridRef.current?.api?.paginationGoToPreviousPage?.()}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                        aria-label="Previous page"
                      >
                        {'<'}
                      </button>
                      <div className="px-1 min-w-[76px] text-center">
                        <div className="text-xs font-semibold text-slate-600 leading-none">Page</div>
                        <div className="text-sm font-semibold text-slate-800 tabular-nums leading-tight mt-1">
                          {(paginationInfo.currentPage + 1).toLocaleString()} of {paginationInfo.totalPages.toLocaleString()}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => gridRef.current?.api?.paginationGoToNextPage?.()}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                        aria-label="Next page"
                      >
                        {'>'}
                      </button>
                      <button
                        type="button"
                        onClick={() => gridRef.current?.api?.paginationGoToLastPage?.()}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                        aria-label="Last page"
                      >
                        {'>>'}
                      </button>
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center">
                      <div className="text-xs font-semibold text-slate-600 leading-none">Viewing:</div>
                      <div className="text-sm font-semibold text-slate-800 tabular-nums leading-tight mt-1">
                        {displayedItemCount.toLocaleString()} of {inventory.length.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div
                    className="ag-theme-quartz mx-auto"
                    style={{
                      height: 'auto',
                      width: salesGridWidth,
                    }}
                  >
                    <AgGridReact
                      ref={gridRef}
                      rowData={filteredInventory}
                      columnDefs={quickColumnDefs}
                      domLayout="autoHeight"
                      pagination={true}
                      paginationPageSize={50}
                      paginationPageSizeSelector={false}
                      suppressPaginationPanel={true}
                      paginationNumberFormatter={(params) =>
                        `${params.value.toLocaleString()}`
                      }
                      suppressAnimationFrame={true}
                      onCellValueChanged={handleCellUpdate}
                      stopEditingWhenCellsLoseFocus={false}
                      singleClickEdit={false}
                      suppressDragLeaveHidesColumns={false}
                      suppressHorizontalScroll={false}
                      defaultColDef={{ resizable: true }}
                      suppressRowClickSelection={true}
                      rowSelection={{ mode: 'multiRow', headerCheckbox: false }}
                      selectionColumnDef={{
                        width: 48,
                        hide: viewMode !== 'unsold',
                        headerClass: 'selection-checkbox-header',
                        cellClass: 'selection-checkbox-cell',
                      }}
                      isRowSelectable={(node) =>
                        viewMode === 'unsold'
                        && String(node?.data?.status || '').toLowerCase() !== 'sold'
                      }
                      onSelectionChanged={handleSellSelectionChanged}
                      onGridReady={onGridReady}
                      onModelUpdated={handleGridModelUpdated}
                      onPaginationChanged={handlePaginationChanged}
                      getRowId={(params) => params.data.id}
                      headerHeight={38}
                      suppressMenuHide={true}
                      rowHeight={60}
                      className="ag-left-aligned-pagination"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No sale selected */}
          {!loading && !selectedSaleId && (
            <div className="text-center py-8">
              <p className="text-gray-500 text-lg">
                Please select an active sale to view inventory.
              </p>
            </div>
          )}


        </div>
      </div>

      <SoldScanModal
        isOpen={scanModalOpen}
        scanError={scanError}
        hasMultipleSales={hasMultipleSales}
        showScanner={showScanner}
        scanInProgress={scanInProgress}
        scanItems={scanItems}
        manualLines={manualLines}
        manualOpen={manualOpen}
        saleDiscountPct={saleDiscPct}
        stripeCheckoutEnabled={saleCcCollectionEnabled}
        scanBaseTotal={scanSubtotal}
        scanTaxAmount={scanSalesTaxAmount}
        manualTitleOptions={manualTitleOptions}
        manualTitlePriceByTitle={manualTitlePriceByTitle}
        categoryOptions={categoryOptions}
        scanErrorItemId={scanErrorItemId}
        onClose={handleCloseScanModal}
        onStartScan={startScan}
        onStopScan={stopScan}
        onRemoveScanItem={removeScanItem}
        onUpdateScanItem={updateScanItem}
        onOpenManualItems={openManualItems}
        onAddManualLine={addManualLine}
        onUpdateManualLine={handleUpdateManualLine}
        onRemoveManualLine={removeManualLine}
        onConfirm={handleConfirmScanAndClear}
      />

      {/* Simple image modal */}
      {showModal && selectedImage && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="relative bg-black/80 rounded-lg p-3 max-w-3xl max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100/80 hover:bg-gray-200 text-gray-800 text-xl font-bold"
              aria-label="Close image"
            >
              ×
            </button>
            <img
              src={selectedImage}
              alt="Inventory item full size"
              className="max-w-full max-h-[80vh] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default SoldInv;
